import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useUserCompanies } from '../hooks/useCompanies';

const STORAGE_KEY = 'rely_selected_company_id';

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  // Use React Query hook for companies data
  const { companies, isLoading, error, refreshCompanies, isRefetching } =
    useUserCompanies();

  const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || null;
  });

  // Derive selected company from companies list
  const selectedCompany =
    companies.find((c) => c.id === selectedCompanyId) || null;

  // Validate and auto-select company when companies list changes
  useEffect(() => {
    if (isLoading || companies.length === 0) return;

    // Only check validation if we don't have a valid selection already
    const currentSelection = selectedCompanyId;
    const isCurrentSelectionValid =
      currentSelection && companies.some((c) => c.id === currentSelection);

    if (isCurrentSelectionValid) {
      // Current selection is valid, no action needed
      return;
    }

    // Check localStorage for a saved selection
    const savedId = localStorage.getItem(STORAGE_KEY);
    const isSavedSelectionValid =
      savedId && companies.some((c) => c.id === savedId);

    if (isSavedSelectionValid && savedId !== currentSelection) {
      // Restore saved selection
      setSelectedCompanyId(savedId);
    } else if (!isSavedSelectionValid) {
      // Auto-select first company if no valid selection exists
      const firstCompanyId = companies[0].id;
      setSelectedCompanyId(firstCompanyId);
      localStorage.setItem(STORAGE_KEY, firstCompanyId);
    }
  }, [companies, isLoading, selectedCompanyId]);

  // Listen for storage changes from other tabs
  // DISABLED: This was causing infinite loops when multiple tabs had different URLs
  // Each tab would try to sync with localStorage, creating a ping-pong effect
  // The URL should be the source of truth, not localStorage
  // useEffect(() => {
  //   const handleStorageChange = (e) => {
  //     if (e.key === STORAGE_KEY && e.newValue !== selectedCompanyId) {
  //       setSelectedCompanyId(e.newValue);
  //     }
  //   };
  //   window.addEventListener('storage', handleStorageChange);
  //   return () => window.removeEventListener('storage', handleStorageChange);
  // }, [selectedCompanyId]);

  // Set selected company and persist to localStorage
  const setSelectedCompany = useCallback(
    (companyId) => {
      if (companyId) {
        setSelectedCompanyId(companyId);
        localStorage.setItem(STORAGE_KEY, companyId);
      } else {
        setSelectedCompanyId(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    },
    []
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedCompanyId(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Check if a company is in an active/completed state
  const isCompanyActive = useCallback((company) => {
    const activeStages = [
      'delivered',
      'completed',
      'formation_completed',
      'DELIVERED',
      'COMPLETED',
    ];
    return activeStages.includes(
      company?.status?.toLowerCase?.() || company?.status
    );
  }, []);

  // Get formation progress percentage
  const getFormationProgress = useCallback((company) => {
    const stageOrder = [
      'initiated',
      'payment_pending',
      'payment_completed',
      'details_pending',
      'under_review',
      'filing',
      'delivered',
    ];
    const currentIndex = stageOrder.findIndex(
      (s) => s.toLowerCase() === (company?.status?.toLowerCase?.() || '')
    );
    if (currentIndex === -1) return 0;
    return Math.round(((currentIndex + 1) / stageOrder.length) * 100);
  }, []);

  const value = useMemo(
    () => ({
      companies,
      selectedCompanyId,
      selectedCompany,
      isLoading, // Only true during initial load, not refetches
      isRefetching, // True during background refetches
      error: error?.message || null,
      setSelectedCompany,
      refreshCompanies,
      clearSelection,
      isCompanyActive,
      getFormationProgress,
      hasCompanies: companies.length > 0,
    }),
    [
      companies,
      selectedCompanyId,
      selectedCompany,
      isLoading,
      isRefetching,
      error,
      setSelectedCompany,
      refreshCompanies,
      clearSelection,
      isCompanyActive,
      getFormationProgress,
    ]
  );

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}

export default CompanyContext;
